/**
 * WorkflowBuilder.tsx
 *
 * Visual, no-code composer: drag pre-built marketplace skills into a vertical
 * flow, configure each step's inputs from its JSON Schema, then save the result
 * as a deployed agent or run it immediately.
 *
 * ── How this maps to the existing API ────────────────────────────────────────
 * The platform has no declarative multi-step pipeline format: a run executes a
 * single initial step (from its `input`), and longer DAGs emerge at runtime as
 * each skill emits its own next step. So:
 *
 *   • Save as Agent → POST /v1/agents  →  POST /v1/agents/{id}/versions
 *                     →  POST /v1/agents/{id}/deploy
 *     The full step sequence is persisted in agent_version.runtimeConfig.workflow
 *     as a forward-looking definition (round-trips through the real API).
 *
 *   • Run Now → ensures the agent exists, then POST /v1/runs with the FIRST
 *     step's skill + config as the run input.
 *
 * IMPORTANT: the backend does not yet execute the saved sequence as a pipeline —
 * only the first step runs today. This is surfaced in the UI (see CaveatNote).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GripVertical, Plus, Trash2, Play, Save, Search } from 'lucide-react'

import { api, ApiError, apiErrorMessage } from '../api/client'
import { TEMPLATES } from './workflowTemplates'
import { useWriteAccess } from '../lib/writeAccess'

// ── Types ─────────────────────────────────────────────────────────────────────

interface MarketplaceSkill {
  skillId: string
  version: string
  name: string
  description?: string
  category?: string
  capabilities?: string[]
  inputSchema?: JSONSchema
}

interface JSONSchema {
  type?: string
  properties?: Record<string, JSONSchemaProp>
  required?: string[]
}

interface JSONSchemaProp {
  type?: string
  title?: string
  description?: string
}

interface WorkflowStep {
  uid: string
  skillId: string
  version: string
  skillName: string
  category: string
  inputSchema?: JSONSchema
  config: Record<string, unknown>
}

type LoadState = 'loading' | 'ready' | 'error'

type Status =
  | { kind: 'idle' }
  | { kind: 'busy'; message: string }
  | { kind: 'ok'; message: string }
  | { kind: 'error'; message: string }

// ── Helpers ───────────────────────────────────────────────────────────────────

let uidSeq = 0
const nextUid = () => `step-${++uidSeq}-${Date.now().toString(36)}`

function slugify(name: string): string {
  const base = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48)
  // Suffix keeps (project_id, slug) unique across repeated saves.
  return `${base || 'workflow'}-${Date.now().toString(36)}`
}

function schemaFields(schema?: JSONSchema): Array<{ key: string; prop: JSONSchemaProp; required: boolean }> {
  if (!schema?.properties) return []
  const required = new Set(schema.required ?? [])
  return Object.entries(schema.properties).map(([key, prop]) => ({
    key,
    prop,
    required: required.has(key),
  }))
}

// ── Model options (variant selector for A/B testing) ──────────────────────────

const MODEL_OPTIONS: ReadonlyArray<{ label: string; provider: string; model: string }> = [
  { label: 'Claude Sonnet 4',  provider: 'anthropic', model: 'claude-sonnet-4-20250514' },
  { label: 'Claude Haiku 4.5', provider: 'anthropic', model: 'claude-haiku-4-5-20251001' },
  { label: 'GPT-4o',           provider: 'openai',    model: 'gpt-4o' },
  { label: 'GPT-4o Mini',      provider: 'openai',    model: 'gpt-4o-mini' },
  { label: 'Gemini 2.0 Flash', provider: 'gemini',    model: 'gemini-2.0-flash' },
]
const modelOptionKey = (o: { provider: string; model: string }) => `${o.provider}/${o.model}`

// ── Shared field styling (matches AgentCreatePage) ────────────────────────────

const FIELD_INPUT =
  'w-full rounded-[9px] border border-[rgba(148,163,184,0.15)] bg-[rgba(6,10,16,0.55)] text-[12px] text-[#c8d9ee] leading-[1.55] px-[12px] py-[9px] outline-none transition-colors duration-[120ms] placeholder:text-[#2c3f55] focus:border-[rgba(56,189,248,0.4)]'

// ── Caveat note ─────────────────────────────────────────────────────────────

function CaveatNote() {
  return (
    <div className="text-[11px] text-[#8a6d3b] bg-[rgba(245,158,11,0.08)] border border-[rgba(245,158,11,0.22)] rounded-[8px] px-3 py-2 leading-[1.5]">
      <span className="font-bold text-[#fbbf24]">Preview:</span> the full sequence is saved
      with the agent, but the backend currently executes only the <span className="font-semibold">first step</span> when
      you run. Multi-step pipeline execution is coming soon.
    </div>
  )
}

// ── Skill library (left) ──────────────────────────────────────────────────────

function SkillLibrary({
  skills,
  loadState,
  onAdd,
  onReload,
}: {
  skills: MarketplaceSkill[]
  loadState: LoadState
  onAdd: (skill: MarketplaceSkill) => void
  onReload: () => void
}) {
  const [query, setQuery] = useState('')

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase()
    const filtered = q
      ? skills.filter((s) => s.name.toLowerCase().includes(q) || (s.description ?? '').toLowerCase().includes(q))
      : skills
    const map = new Map<string, MarketplaceSkill[]>()
    for (const s of filtered) {
      const cat = s.category || 'Uncategorised'
      const arr = map.get(cat) ?? []
      arr.push(s)
      map.set(cat, arr)
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [skills, query])

  return (
    <div className="w-[300px] flex-shrink-0 border-r border-[rgba(148,163,184,0.12)] flex flex-col bg-[rgba(8,13,20,0.5)]">
      <div className="px-4 pt-4 pb-3 border-b border-[rgba(148,163,184,0.1)]">
        <h2 className="text-[13px] font-[800] text-[#dce9f7] m-0 mb-[10px] tracking-[-0.01em]">Skill Library</h2>
        <div className="relative">
          <span className="absolute left-[9px] top-1/2 -translate-y-1/2 text-[#3a5068]" aria-hidden="true">
            <Search size={13} />
          </span>
          <input
            className={`${FIELD_INPUT} pl-[28px]`}
            placeholder="Search skills…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto sds-body-scroll px-3 py-3 flex flex-col gap-4">
        {loadState === 'loading' && (
          <div className="flex flex-col gap-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-[58px] rounded-[10px] border border-[rgba(148,163,184,0.08)] bg-[rgba(11,18,32,0.5)] animate-pulse" />
            ))}
          </div>
        )}

        {loadState === 'error' && (
          <div className="text-[12px] text-[#f87171] leading-[1.5]">
            Failed to load skills.{' '}
            <button className="underline bg-transparent border-none text-[#f87171] cursor-pointer p-0" onClick={onReload}>
              Retry
            </button>
          </div>
        )}

        {loadState === 'ready' && skills.length === 0 && (
          <div className="text-[12px] text-[#4e6480] leading-[1.6]">
            No skills available. Run <code className="font-mono text-[#8fa3bd]">make marketplace-smoke</code> to seed the marketplace.
          </div>
        )}

        {loadState === 'ready' &&
          grouped.map(([category, items]) => (
            <div key={category} className="flex flex-col gap-2">
              <span className="text-[9.5px] font-[800] tracking-[0.12em] uppercase text-[#3a5068] px-1">{category}</span>
              {items.map((skill) => (
                <div
                  key={`${skill.skillId}@${skill.version}`}
                  draggable
                  onDragStart={(e) => {
                    e.dataTransfer.setData('application/x-skill-id', `${skill.skillId}@${skill.version}`)
                    e.dataTransfer.effectAllowed = 'copy'
                  }}
                  onClick={() => onAdd(skill)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onAdd(skill) }}
                  className="group cursor-grab active:cursor-grabbing rounded-[10px] border border-[rgba(148,163,184,0.12)] bg-[rgba(11,18,32,0.6)] px-3 py-[10px] transition-colors duration-[120ms] hover:border-[rgba(56,189,248,0.35)] hover:bg-[rgba(56,189,248,0.06)]"
                  title="Drag onto the canvas or click to add"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-[12.5px] font-bold text-[#c8d9ee] truncate">{skill.name}</span>
                    <span className="text-[#3a5068] opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true">
                      <Plus size={13} />
                    </span>
                  </div>
                  {skill.description && (
                    <p className="text-[10.5px] text-[#4e6480] leading-[1.5] m-0 mt-0.5 line-clamp-2">{skill.description}</p>
                  )}
                </div>
              ))}
            </div>
          ))}
      </div>
    </div>
  )
}

// ── Canvas (centre) ───────────────────────────────────────────────────────────

function Canvas({
  steps,
  selectedUid,
  onSelect,
  onDelete,
  onReorder,
  onDropSkill,
}: {
  steps: WorkflowStep[]
  selectedUid: string | null
  onSelect: (uid: string) => void
  onDelete: (uid: string) => void
  onReorder: (from: number, to: number) => void
  onDropSkill: (skillKey: string) => void
}) {
  const dragIndex = useRef<number | null>(null)

  return (
    <div
      className="flex-1 overflow-y-auto sds-body-scroll px-8 py-7 min-w-0"
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('application/x-skill-id')) e.preventDefault()
      }}
      onDrop={(e) => {
        const skillKey = e.dataTransfer.getData('application/x-skill-id')
        if (skillKey) {
          e.preventDefault()
          onDropSkill(skillKey)
        }
      }}
    >
      <div className="max-w-[560px] mx-auto flex flex-col items-center">
        {steps.length === 0 && (
          <div className="mt-16 text-center text-[#4e6480]">
            <div className="text-[34px] opacity-30 leading-none mb-3" aria-hidden="true">⬡</div>
            <p className="text-[14px] font-bold text-[#6b849e] m-0 mb-1">Start your workflow</p>
            <p className="text-[12px] m-0 leading-[1.6]">Drag a skill from the library, or click one to add it as the first step.</p>
          </div>
        )}

        {steps.map((step, i) => (
          <div key={step.uid} className="w-full flex flex-col items-center">
            {i > 0 && <div className="w-px h-6 bg-[rgba(148,163,184,0.25)]" aria-hidden="true" />}
            <div
              draggable
              onDragStart={() => { dragIndex.current = i }}
              onDragOver={(e) => {
                if (dragIndex.current !== null) e.preventDefault()
              }}
              onDrop={(e) => {
                e.preventDefault()
                e.stopPropagation()
                if (dragIndex.current !== null && dragIndex.current !== i) {
                  onReorder(dragIndex.current, i)
                }
                dragIndex.current = null
              }}
              onClick={() => onSelect(step.uid)}
              className={[
                'w-full rounded-[12px] border px-4 py-[14px] cursor-pointer transition-all duration-[140ms] bg-[rgba(11,18,32,0.72)] backdrop-blur-md',
                selectedUid === step.uid
                  ? 'border-[rgba(56,189,248,0.55)] shadow-[0_0_0_1px_rgba(56,189,248,0.25),0_4px_24px_rgba(0,0,0,0.4)]'
                  : 'border-[rgba(148,163,184,0.14)] hover:border-[rgba(56,189,248,0.3)]',
              ].join(' ')}
            >
              <div className="flex items-center gap-3">
                <span className="text-[#3a5068] cursor-grab active:cursor-grabbing flex-shrink-0" title="Drag to reorder" aria-hidden="true">
                  <GripVertical size={15} />
                </span>
                <span className="w-[22px] h-[22px] flex-shrink-0 rounded-full bg-[rgba(56,189,248,0.14)] border border-[rgba(56,189,248,0.3)] text-[#38bdf8] text-[11px] font-[800] flex items-center justify-center tabular-nums">
                  {i + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-bold text-[#dce9f7] truncate">{step.skillName}</div>
                  <div className="text-[10.5px] text-[#4e6480] truncate font-mono">
                    {summariseConfig(step)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onDelete(step.uid) }}
                  className="flex-shrink-0 w-7 h-7 flex items-center justify-center rounded-[7px] border-none bg-transparent text-[#4e6480] cursor-pointer transition-colors hover:bg-[rgba(239,68,68,0.12)] hover:text-[#f87171]"
                  title="Remove step"
                  aria-label={`Remove step ${i + 1}`}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function summariseConfig(step: WorkflowStep): string {
  const keys = Object.keys(step.config).filter((k) => {
    const v = step.config[k]
    return v !== '' && v !== undefined && v !== null
  })
  if (keys.length === 0) return step.skillId
  return keys.map((k) => `${k}=${String(step.config[k])}`).join('  ·  ').slice(0, 80)
}

// ── Config panel (right) ──────────────────────────────────────────────────────

function ConfigPanel({
  step,
  stepIndex,
  priorSteps,
  onChange,
}: {
  step: WorkflowStep | null
  stepIndex: number
  priorSteps: WorkflowStep[]
  onChange: (uid: string, key: string, value: unknown) => void
}) {
  if (!step) {
    return (
      <div className="w-[320px] flex-shrink-0 border-l border-[rgba(148,163,184,0.12)] bg-[rgba(8,13,20,0.5)] flex items-center justify-center px-6">
        <p className="text-[12px] text-[#4e6480] text-center leading-[1.6]">Select a step to configure its inputs.</p>
      </div>
    )
  }

  const fields = schemaFields(step.inputSchema)

  return (
    <div className="w-[320px] flex-shrink-0 border-l border-[rgba(148,163,184,0.12)] bg-[rgba(8,13,20,0.5)] flex flex-col">
      <div className="px-4 pt-4 pb-3 border-b border-[rgba(148,163,184,0.1)]">
        <span className="text-[9.5px] font-[800] tracking-[0.12em] uppercase text-[#3a5068]">Step {stepIndex + 1}</span>
        <h2 className="text-[14px] font-[800] text-[#dce9f7] m-0 mt-0.5 tracking-[-0.01em]">{step.skillName}</h2>
      </div>

      <div className="flex-1 overflow-y-auto sds-body-scroll px-4 py-4 flex flex-col gap-4">
        {fields.length === 0 && (
          <p className="text-[12px] text-[#4e6480] leading-[1.6] italic">This skill declares no configurable inputs.</p>
        )}

        {fields.map(({ key, prop, required }) => (
          <div key={key} className="flex flex-col gap-[6px]">
            <label className="text-[11px] font-bold text-[#8fa3bd] tracking-[0.02em] flex items-center gap-1.5" htmlFor={`f-${step.uid}-${key}`}>
              {prop.title || key}
              {required && <span className="text-[#f87171]" title="required">*</span>}
              <span className="text-[9px] font-mono font-normal text-[#3a5068] normal-case">{prop.type}</span>
            </label>
            {prop.description && <span className="text-[10px] text-[#4e6480] leading-[1.4] -mt-0.5">{prop.description}</span>}

            <SchemaField
              id={`f-${step.uid}-${key}`}
              prop={prop}
              value={step.config[key]}
              onChange={(v) => onChange(step.uid, key, v)}
            />

            {/* Reference helper — only meaningful for string fields and when prior steps exist */}
            {isTextType(prop.type) && priorSteps.length > 0 && (
              <select
                className="text-[10px] rounded-[7px] border border-[rgba(148,163,184,0.14)] bg-[rgba(6,10,16,0.55)] text-[#8fa3bd] px-2 py-1 outline-none cursor-pointer"
                value=""
                onChange={(e) => {
                  if (!e.target.value) return
                  const cur = typeof step.config[key] === 'string' ? (step.config[key] as string) : ''
                  onChange(step.uid, key, `${cur}{{${e.target.value}.output.}}`)
                  e.target.value = ''
                }}
                aria-label="Insert reference to a previous step output"
              >
                <option value="">↪ reference a previous step…</option>
                {priorSteps.map((p, idx) => (
                  <option key={p.uid} value={`step_${idx + 1}`}>
                    step_{idx + 1} · {p.skillName}
                  </option>
                ))}
              </select>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

function isTextType(type?: string): boolean {
  return type === undefined || type === 'string'
}

function SchemaField({
  id,
  prop,
  value,
  onChange,
}: {
  id: string
  prop: JSONSchemaProp
  value: unknown
  onChange: (value: unknown) => void
}) {
  if (prop.type === 'boolean') {
    const checked = value === true
    return (
      <button
        id={id}
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={() => onChange(!checked)}
        className={[
          'relative w-[42px] h-[24px] rounded-full transition-colors duration-[140ms] flex-shrink-0',
          checked ? 'bg-[rgba(34,197,94,0.5)]' : 'bg-[rgba(148,163,184,0.2)]',
        ].join(' ')}
      >
        <span
          className={[
            'absolute top-[2px] w-[20px] h-[20px] rounded-full bg-white transition-transform duration-[140ms]',
            checked ? 'translate-x-[20px]' : 'translate-x-[2px]',
          ].join(' ')}
        />
      </button>
    )
  }

  if (prop.type === 'integer' || prop.type === 'number') {
    return (
      <input
        id={id}
        type="number"
        className={FIELD_INPUT}
        value={value === undefined || value === null ? '' : String(value)}
        step={prop.type === 'integer' ? 1 : 'any'}
        onChange={(e) => {
          const raw = e.target.value
          if (raw === '') { onChange(undefined); return }
          const n = prop.type === 'integer' ? parseInt(raw, 10) : parseFloat(raw)
          onChange(Number.isNaN(n) ? undefined : n)
        }}
      />
    )
  }

  // string + fallback
  return (
    <input
      id={id}
      type="text"
      className={FIELD_INPUT}
      value={typeof value === 'string' ? value : ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder="value or {{step_1.output.field}}"
    />
  )
}

// ── Bottom bar ────────────────────────────────────────────────────────────────

function BottomBar({
  name,
  projectId,
  status,
  canSubmit,
  abEnabled,
  variantModel,
  onNameChange,
  onProjectChange,
  onAbToggle,
  onVariantModelChange,
  onSave,
  onRun,
}: {
  name: string
  projectId: string
  status: Status
  canSubmit: boolean
  abEnabled: boolean
  variantModel: string
  onNameChange: (v: string) => void
  onProjectChange: (v: string) => void
  onAbToggle: (v: boolean) => void
  onVariantModelChange: (v: string) => void
  /** Absent for a role without records access (agent writes): hidden. */
  onSave?: () => void
  onRun?: () => void
}) {
  const busy = status.kind === 'busy'
  return (
    <div className="flex-shrink-0 border-t border-[rgba(148,163,184,0.13)] bg-[rgba(8,13,20,0.85)] backdrop-blur-md px-6 py-3 flex items-center gap-4">
      <input
        className={`${FIELD_INPUT} max-w-[220px]`}
        placeholder="Agent name"
        value={name}
        onChange={(e) => onNameChange(e.target.value)}
      />
      <input
        className={`${FIELD_INPUT} max-w-[260px] font-mono text-[11px]`}
        placeholder="Project ID (UUID)"
        value={projectId}
        onChange={(e) => onProjectChange(e.target.value)}
        spellCheck={false}
      />

      {/* A/B test toggle + variant model selector */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          type="button"
          role="switch"
          aria-checked={abEnabled}
          onClick={() => onAbToggle(!abEnabled)}
          className="inline-flex items-center gap-[6px] text-[11px] font-bold text-[#8fa3bd] cursor-pointer bg-transparent border-none"
          title="Run the same workflow against two models for comparison"
        >
          <span
            className={[
              'relative w-[34px] h-[19px] rounded-full transition-colors duration-[140ms]',
              abEnabled ? 'bg-[rgba(167,139,250,0.5)]' : 'bg-[rgba(148,163,184,0.2)]',
            ].join(' ')}
          >
            <span className={['absolute top-[2px] w-[15px] h-[15px] rounded-full bg-white transition-transform duration-[140ms]', abEnabled ? 'translate-x-[17px]' : 'translate-x-[2px]'].join(' ')} />
          </span>
          A/B Test
        </button>
        {abEnabled && (
          <select
            className={`${FIELD_INPUT} max-w-[180px]`}
            value={variantModel}
            onChange={(e) => onVariantModelChange(e.target.value)}
            title="Variant model"
          >
            {MODEL_OPTIONS.map((o) => (
              <option key={modelOptionKey(o)} value={modelOptionKey(o)}>
                vs {o.label}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="flex-1 min-w-0">
        {status.kind !== 'idle' && (
          <span
            className={[
              'text-[11.5px] truncate',
              status.kind === 'error' ? 'text-[#f87171]' : status.kind === 'ok' ? 'text-[#4ade80]' : 'text-[#8fa3bd]',
            ].join(' ')}
          >
            {status.message}
          </span>
        )}
      </div>

      {onSave && <button
        type="button"
        disabled={!canSubmit || busy}
        onClick={onSave}
        className="inline-flex items-center gap-[7px] px-[14px] py-[9px] rounded-[9px] border border-[rgba(148,163,184,0.2)] bg-[rgba(148,163,184,0.06)] text-[#c8d9ee] text-[12px] font-bold cursor-pointer transition-colors hover:enabled:bg-[rgba(148,163,184,0.12)] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Save size={14} /> Save as Agent
      </button>}
      {onRun && <button
        type="button"
        disabled={!canSubmit || busy}
        onClick={onRun}
        className="inline-flex items-center gap-[7px] px-[16px] py-[9px] rounded-[9px] border border-[rgba(34,197,94,0.4)] bg-[rgba(34,197,94,0.16)] text-[#4ade80] text-[12px] font-[800] cursor-pointer transition-colors hover:enabled:bg-[rgba(34,197,94,0.26)] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <Play size={14} /> Run Now
      </button>}
    </div>
  )
}

// ── Template picker (shown on an empty canvas) ────────────────────────────────

function TemplatePicker({
  hasSkills,
  onUse,
  onScratch,
}: {
  hasSkills: boolean
  onUse: (skillIds: string[]) => void
  onScratch: () => void
}) {
  return (
    <div className="flex-1 overflow-y-auto sds-body-scroll flex items-center justify-center px-8 py-8">
      <div className="w-full max-w-[640px] flex flex-col items-center">
        <h2 className="text-[18px] font-[900] text-[#dce9f7] m-0 tracking-[-0.02em]">Start from a template</h2>
        <p className="text-[12px] text-[#4e6480] m-0 mt-1.5 mb-6 text-center">
          Pick a starting point — you can edit every step afterwards.
        </p>

        {!hasSkills && (
          <div className="mb-5 text-[11.5px] text-[#8a6d3b] bg-[rgba(245,158,11,0.08)] border border-[rgba(245,158,11,0.22)] rounded-[8px] px-3 py-2 leading-[1.5] max-w-[520px]">
            <span className="font-bold text-[#fbbf24]">No marketplace skills available.</span> Templates load real
            skills — run <code className="font-mono">make marketplace-smoke</code> to seed and verify them first.
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 w-full">
          {TEMPLATES.map((t) => {
            const Icon = t.icon
            return (
              <div
                key={t.id}
                className="rounded-[12px] border border-[rgba(148,163,184,0.14)] bg-[rgba(11,18,32,0.6)] p-4 flex flex-col gap-2 transition-colors duration-[120ms] hover:border-[rgba(56,189,248,0.35)]"
              >
                <div className="flex items-center gap-2.5">
                  <span className="w-[34px] h-[34px] rounded-[9px] bg-[rgba(56,189,248,0.12)] border border-[rgba(56,189,248,0.25)] text-[#38bdf8] flex items-center justify-center flex-shrink-0" aria-hidden="true">
                    <Icon size={17} />
                  </span>
                  <h3 className="text-[13.5px] font-[800] text-[#dce9f7] m-0">{t.name}</h3>
                </div>
                <p className="text-[11.5px] text-[#4e6480] leading-[1.55] m-0 flex-1">{t.description}</p>
                <div className="text-[10px] text-[#3a5068] font-mono truncate">{t.skillIds.join(' → ')}</div>
                <button
                  type="button"
                  disabled={!hasSkills}
                  onClick={() => onUse(t.skillIds)}
                  className="self-start mt-1 px-[12px] py-[7px] rounded-[8px] border border-[rgba(56,189,248,0.4)] bg-[rgba(56,189,248,0.12)] text-[#38bdf8] text-[11px] font-[800] cursor-pointer transition-colors hover:enabled:bg-[rgba(56,189,248,0.2)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Use template
                </button>
              </div>
            )
          })}
        </div>

        <button
          type="button"
          onClick={onScratch}
          className="mt-6 text-[12px] text-[#4e6480] underline bg-transparent border-none cursor-pointer hover:text-[#8fa3bd] transition-colors"
        >
          Start from scratch
        </button>
      </div>
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────

export function WorkflowBuilder() {
  const [skills, setSkills] = useState<MarketplaceSkill[]>([])
  const [loadState, setLoadState] = useState<LoadState>('loading')
  const [steps, setSteps] = useState<WorkflowStep[]>([])
  const [selectedUid, setSelectedUid] = useState<string | null>(null)
  const [name, setName] = useState('')
  // Save as Agent and Run Now both create + deploy an agent: owner-or-admin
  // writes (records access, lib/writeAccess; fail closed).
  const { records } = useWriteAccess()
  const [projectId, setProjectId] = useState('')
  const [status, setStatus] = useState<Status>({ kind: 'idle' })
  // A/B testing: when enabled, Run Now submits a control + variant pair.
  const [abEnabled, setAbEnabled] = useState(false)
  const [variantModel, setVariantModel] = useState<string>(modelOptionKey(MODEL_OPTIONS[1]))
  // Template picker: shown on an empty canvas until used or dismissed.
  const [dismissedPicker, setDismissedPicker] = useState(false)

  // Cache the deployed agent so a Save followed by Run reuses it.
  const savedAgentId = useRef<string | null>(null)

  // ── Load skills ──────────────────────────────────────────────────────────
  const loadSkills = useCallback(async () => {
    setLoadState('loading')
    try {
      const data = await api.get<{ items: MarketplaceSkill[] | null }>('/v1/marketplace/skills')
      setSkills(Array.isArray(data.items) ? data.items : [])
      setLoadState('ready')
    } catch {
      setLoadState('error')
    }
  }, [])

  useEffect(() => { void loadSkills() }, [loadSkills])

  // ── Step mutations (invalidate the saved agent on any change) ──────────────
  const invalidate = () => { savedAgentId.current = null }

  const addSkill = useCallback((skill: MarketplaceSkill) => {
    invalidate()
    const step: WorkflowStep = {
      uid: nextUid(),
      skillId: skill.skillId,
      version: skill.version,
      skillName: skill.name,
      category: skill.category || 'Uncategorised',
      inputSchema: skill.inputSchema,
      config: {},
    }
    setSteps((prev) => [...prev, step])
    setSelectedUid(step.uid)
  }, [])

  const addSkillByKey = useCallback((skillKey: string) => {
    const skill = skills.find((s) => `${s.skillId}@${s.version}` === skillKey)
    if (skill) addSkill(skill)
  }, [skills, addSkill])

  // Load a template: resolve each skill ID against the live marketplace so each
  // step gets the skill's real version/inputSchema. Missing skills are skipped.
  const loadTemplate = useCallback((skillIds: string[]) => {
    invalidate()
    const built: WorkflowStep[] = []
    for (const id of skillIds) {
      const skill = skills.find((s) => s.skillId === id)
      if (!skill) continue
      built.push({
        uid: nextUid(),
        skillId: skill.skillId,
        version: skill.version,
        skillName: skill.name,
        category: skill.category || 'Uncategorised',
        inputSchema: skill.inputSchema,
        config: {},
      })
    }
    if (built.length === 0) return
    setSteps(built)
    setSelectedUid(built[0].uid)
  }, [skills])

  const deleteStep = useCallback((uid: string) => {
    invalidate()
    setSteps((prev) => prev.filter((s) => s.uid !== uid))
    setSelectedUid((cur) => (cur === uid ? null : cur))
  }, [])

  const reorderStep = useCallback((from: number, to: number) => {
    invalidate()
    setSteps((prev) => {
      const next = [...prev]
      const [moved] = next.splice(from, 1)
      next.splice(to, 0, moved)
      return next
    })
  }, [])

  const setConfigValue = useCallback((uid: string, key: string, value: unknown) => {
    invalidate()
    setSteps((prev) =>
      prev.map((s) => {
        if (s.uid !== uid) return s
        const config = { ...s.config }
        if (value === undefined) delete config[key]
        else config[key] = value
        return { ...s, config }
      }),
    )
  }, [])

  const selectedIndex = steps.findIndex((s) => s.uid === selectedUid)
  const selectedStep = selectedIndex >= 0 ? steps[selectedIndex] : null
  const priorSteps = selectedIndex > 0 ? steps.slice(0, selectedIndex) : []

  const canSubmit = steps.length > 0 && name.trim() !== '' && projectId.trim() !== ''

  // ── Save: create agent → version (workflow in runtimeConfig) → deploy ──────
  const saveAsAgent = useCallback(async (): Promise<string> => {
    if (savedAgentId.current) return savedAgentId.current

    const workflow = {
      version: 1,
      steps: steps.map((s, i) => ({
        stepIndex: i,
        skillId: s.skillId,
        skillVersion: s.version,
        input: s.config,
      })),
    }
    const instructions =
      `Workflow with ${steps.length} step(s): ` +
      steps.map((s, i) => `${i + 1}. ${s.skillName}`).join(' → ')

    setStatus({ kind: 'busy', message: 'Creating agent…' })
    const agent = await api.post<{ id: string }>('/v1/agents', {
      projectId: projectId.trim(),
      slug: slugify(name),
      name: name.trim(),
      executionMode: 'async',
    })

    setStatus({ kind: 'busy', message: 'Saving workflow version…' })
    const version = await api.post<{ id: string }>(
      `/v1/agents/${encodeURIComponent(agent.id)}/versions`,
      { instructions, runtimeConfig: { workflow } },
    )

    setStatus({ kind: 'busy', message: 'Deploying…' })
    await api.post(`/v1/agents/${encodeURIComponent(agent.id)}/deploy`, { agentVersionId: version.id })

    savedAgentId.current = agent.id
    return agent.id
  }, [steps, name, projectId])

  const handleSave = useCallback(async () => {
    if (!canSubmit) return
    try {
      const agentId = await saveAsAgent()
      setStatus({ kind: 'ok', message: `Saved & deployed agent ${agentId.slice(0, 8)}… (${steps.length} steps)` })
    } catch (err) {
      setStatus({ kind: 'error', message: errorMessage(err, 'Failed to save agent') })
    }
  }, [canSubmit, saveAsAgent, steps.length])

  const handleRun = useCallback(async () => {
    if (!canSubmit) return
    try {
      const agentId = await saveAsAgent()
      // Run input maps to the FIRST step only (the backend executes one step).
      const first = steps[0]
      const input: Record<string, unknown> = {
        skill_id: first.skillId,
        skill_version: first.version,
        ...first.config,
      }
      setStatus({ kind: 'busy', message: 'Submitting run…' })

      if (abEnabled) {
        // A/B: the endpoint creates a control (agent's model) + variant pair.
        const [variantProvider, variantModelName] = variantModel.split('/', 2)
        const res = await api.post<{ control_run_id: string; variant_run_id: string }>('/v1/runs', {
          agentId,
          triggerType: 'manual',
          input,
          ab_test: { variant_provider: variantProvider, variant_model: variantModelName },
        })
        setStatus({
          kind: 'ok',
          message: `A/B started — control ${res.control_run_id.slice(0, 8)}… vs variant ${res.variant_run_id.slice(0, 8)}…`,
        })
        return
      }

      const run = await api.post<{ id: string }>('/v1/runs', {
        agentId,
        triggerType: 'manual',
        input,
      })
      setStatus({ kind: 'ok', message: `Run ${run.id.slice(0, 8)}… started (step 1: ${first.skillName})` })
    } catch (err) {
      setStatus({ kind: 'error', message: errorMessage(err, 'Failed to start run') })
    }
  }, [canSubmit, saveAsAgent, steps, abEnabled, variantModel])

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-[calc(100vh-var(--header-h)-150px)] min-h-[520px] border border-[rgba(148,163,184,0.12)] rounded-[var(--radius-md)] overflow-hidden bg-[rgba(6,10,16,0.4)]">
      {/* Header strip with caveat */}
      <div className="flex items-center justify-between gap-4 px-6 py-3 border-b border-[rgba(148,163,184,0.12)]">
        <div>
          <h1 className="text-[16px] font-[900] text-[#dce9f7] m-0 tracking-[-0.02em]">Workflow Builder</h1>
          <p className="text-[11px] text-[#4e6480] m-0">Connect pre-built skills into an agent — no code required.</p>
        </div>
        <div className="max-w-[440px]"><CaveatNote /></div>
      </div>

      {/* Template picker on an empty canvas, else the three-column builder */}
      {loadState === 'ready' && steps.length === 0 && !dismissedPicker ? (
        <TemplatePicker
          hasSkills={skills.length > 0}
          onUse={loadTemplate}
          onScratch={() => setDismissedPicker(true)}
        />
      ) : (
        <div className="flex-1 flex min-h-0">
          <SkillLibrary skills={skills} loadState={loadState} onAdd={addSkill} onReload={loadSkills} />
          <Canvas
            steps={steps}
            selectedUid={selectedUid}
            onSelect={setSelectedUid}
            onDelete={deleteStep}
            onReorder={reorderStep}
            onDropSkill={addSkillByKey}
          />
          <ConfigPanel
            step={selectedStep}
            stepIndex={selectedIndex}
            priorSteps={priorSteps}
            onChange={setConfigValue}
          />
        </div>
      )}

      <BottomBar
        name={name}
        projectId={projectId}
        status={status}
        canSubmit={canSubmit}
        abEnabled={abEnabled}
        variantModel={variantModel}
        onNameChange={setName}
        onProjectChange={setProjectId}
        onAbToggle={setAbEnabled}
        onVariantModelChange={setVariantModel}
        onSave={records ? handleSave : undefined}
        onRun={records ? handleRun : undefined}
      />
    </div>
  )
}

function errorMessage(err: unknown, fallback: string): string {
  // Operator surface: keep the status number (409 vs 500 matters here), but the
  // body goes through apiErrorMessage — never on the wire verbatim. statusText
  // is deliberately dropped: it is often empty under HTTP/2.
  if (err instanceof ApiError) {
    return `${err.status} — ${apiErrorMessage(err, fallback)}`
  }
  return apiErrorMessage(err, fallback)
}
